import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Input,
  Flex,
  Badge,
  Text,
  Button,
  useBreakpointValue,
  useToast
} from '@chakra-ui/react';
import axios from 'axios';
import Swal from 'sweetalert2';

const PaymentTracking = ({ isAdmin = false }) => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const tableMinWidth = useBreakpointValue({ base: "100%", md: "700px" });
  const toast = useToast();

  const fetchPayments = async () => {
    const ticketToken = sessionStorage.getItem('ticketToken');
    try {
      const endpoint = `${process.env.REACT_APP_SERVER_URL}/admin/get-all-payments`;
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${ticketToken}`
        }
      });

      // Filter payments to only include approved, paid, and processing statuses
      const allowedStatuses = ['approved', 'paid', 'processing'];
      const filteredData = response.data.data.filter(payment => 
        allowedStatuses.includes(payment.invoice_status)
      );

      // Sort payments by status priority
      const sortedData = filteredData.sort((a, b) => {
        const statusPriority = {
          'approved': 1,  // Highest priority (unpaid)
          'processing': 2,
          'paid': 3       // Lowest priority
        };
        return statusPriority[a.invoice_status] - statusPriority[b.invoice_status];
      });

      setPayments(sortedData);
      setFilteredPayments(sortedData);
    } catch (error) {
      console.error('Error fetching payments:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch payments',
        timer: 3000,
        showConfirmButton: false
      });
    }
  };

  const handleSendReminder = async (payment) => {
    const ticketToken = sessionStorage.getItem('ticketToken');
    try {
      // First fetch the invoice details to get the email
      const invoiceResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/admin/get-invoice-details/${payment.invoice_number}`,
        {
          headers: {
            Authorization: `Bearer ${ticketToken}`
          }
        }
      );

      const invoiceDetails = invoiceResponse.data;

      // Send the reminder
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/admin/send-payment-reminder`,
        {
          invoiceNo: payment.invoice_number,
          providerEmail: invoiceDetails.data.employerEmail,
          providerName: invoiceDetails.data.employerName,
          amount: payment.amount,
          jobTitle: payment.job_title
        },
        {
          headers: {
            Authorization: `Bearer ${ticketToken}`
          }
        }
      );

      // Success message with SweetAlert2
      await Swal.fire({
        icon: 'success',
        title: 'Reminder Sent',
        text: `Payment reminder sent to ${invoiceDetails.data.employerName}`,
        timer: 3000,
        showConfirmButton: false
      });

    } catch (error) {
      console.error('Error in handleSendReminder:', error);
      console.error('Error response:', error.response?.data);
      
      // Error message with SweetAlert2
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to send reminder',
        timer: 3000,
        showConfirmButton: false
      });
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    let filtered = [...payments]; // Create a copy to avoid mutating original array

    if (filterStatus !== 'all') {
      filtered = filtered.filter(payment => 
        payment.invoice_status === filterStatus
      );
    } else {
      // If showing all, maintain the priority order
      filtered.sort((a, b) => {
        const statusPriority = {
          'approved': 1,
          'processing': 2,
          'paid': 3
        };
        return statusPriority[a.invoice_status] - statusPriority[b.invoice_status];
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(payment =>
        payment.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.provider_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPayments(filtered);
  }, [filterStatus, searchTerm, payments]);

  return (
    <Box p={5}>
      <Flex mb={4} gap={4} flexWrap="wrap">
        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          width="200px"
        >
          <option value="all">All Invoices</option>
          <option value="approved">Approved (Unpaid)</option>
          <option value="processing">Processing</option>
          <option value="paid">Paid</option>
        </Select>

        <Input
          placeholder="Search by invoice number or provider name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          width="300px"
        />
      </Flex>

      <Box overflowX="auto">
        <Table variant="simple" width="100%" minWidth={tableMinWidth}>
          <Thead bgColor="gray.100">
            <Tr>
              <Th>Invoice No</Th>
              <Th>Provider Name</Th>
              <Th>Job Title</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredPayments.map((payment) => (
              <Tr 
                key={payment._id}
                bg={payment.invoice_status === 'approved' ? 'red.50' : 'white'}
              >
                <Td>{payment.invoice_number}</Td>
                <Td>{payment.provider_name}</Td>
                <Td>{payment.job_title}</Td>
                <Td>
                  {payment.currency} {payment.amount}
                </Td>
                <Td>
                  <Badge 
                    colorScheme={payment.invoice_status === 'paid' ? 'green' : payment.invoice_status === 'processing' ? 'yellow' : payment.invoice_status === 'approved' ? 'red' : 'gray'}
                  >
                    {payment.invoice_status === 'paid' ? 'Paid' : payment.invoice_status === 'processing' ? 'Processing' : payment.invoice_status === 'approved' ? 'Unpaid' : 'Nil'}
                  </Badge>
                </Td>
                <Td>
                  {payment.invoice_status === 'approved' && (
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() => handleSendReminder(payment)}
                    >
                      Send Reminder
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default PaymentTracking; 